import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Text,
  Stack,
  Paper,
  Group,
  Badge,
  Select,
  Table,
  Loader,
  Center,
  ThemeIcon,
  SimpleGrid,
  Progress,
  Button,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconClock,
  IconAlertTriangle,
  IconTruckDelivery,
  IconFlask,
  IconTarget,
  IconChartLine,
  IconFileSpreadsheet,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  LineChart,
  Line,
} from "recharts";
import dayjs from "dayjs";
import {
  useStainMetricsSummary,
  useStainMetricsTrends,
} from "../../hooks/useWorkflows";
import { useFacilities } from "../../hooks/useReferenceData";
import { StainMetricsFilters } from "../../services/workflows.service";
import {
  ReportDateRangePicker,
  getLastMonthRange,
  type DateRange,
} from "../../components/reports/ReportDateRangePicker";

const STAIN_TYPE_COLORS: Record<string, string> = {
  "H&E": "var(--mantine-color-blue-6)",
  IHC: "var(--mantine-color-green-6)",
  ISH: "var(--mantine-color-violet-6)",
  Special: "var(--mantine-color-yellow-6)",
  Other: "var(--mantine-color-gray-6)",
};

const STAIN_TYPE_LABELS: Record<string, string> = {
  "H&E": "H&E",
  IHC: "IHC",
  ISH: "ISH",
  Special: "Special Stains",
  Other: "Other",
};

const STAIN_TYPE_OPTIONS = [
  { value: "H&E", label: "H&E" },
  { value: "IHC", label: "IHC" },
  { value: "ISH", label: "ISH" },
  { value: "Special", label: "Special Stains" },
  { value: "Other", label: "Other" },
];

export function StainMetricsContent() {
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    getLastMonthRange()
  );
  const [stainType, setStainType] = useState<string | null>(null);
  const [facilityId, setFacilityId] = useState<string | null>(null);

  const filters: StainMetricsFilters = useMemo(
    () => ({
      fromDate: dayjs(dateRange.startDate).format("YYYY-MM-DD"),
      toDate: dayjs(dateRange.endDate).format("YYYY-MM-DD"),
      stainType: stainType || undefined,
      facilityId: facilityId || undefined,
    }),
    [dateRange, stainType, facilityId]
  );

  const { data: summary, isLoading: summaryLoading } =
    useStainMetricsSummary(filters);
  const { data: trends, isLoading: trendsLoading } =
    useStainMetricsTrends(filters);

  // Dropdown options
  const { data: facilitiesRaw } = useFacilities();

  const facilityOptions = useMemo(
    () =>
      (facilitiesRaw || []).map((f) => ({
        value: f.id,
        label: f.name || "Unknown",
      })),
    [facilitiesRaw]
  );

  // Chart data for TAT by stain type (bar chart)
  const tatChartData = useMemo(() => {
    if (!summary?.turnaroundByStainType) return [];
    return summary.turnaroundByStainType.map((t) => ({
      stainType: STAIN_TYPE_LABELS[t.stainType] || t.stainType,
      "Avg TAT (hrs)": t.avgTatHours,
      "Target (hrs)": t.targetHours,
      "Within Target %": t.withinTargetPct,
    }));
  }, [summary]);

  // Chart data for monthly trends (line chart)
  const trendsChartData = useMemo(() => {
    if (!trends?.months) return [];
    return trends.months.map((m) => ({
      month: dayjs(`${m.month}-01`).format("MMM 'YY"),
      "H&E": m.tatByType["H&E"] || 0,
      IHC: m.tatByType.IHC || 0,
      ISH: m.tatByType.ISH || 0,
      Special: m.tatByType.Special || 0,
      "Rush Cases": m.rushCount,
      "QC Failure %": m.qcFailureRate,
      "Send-Out Avg (days)": m.sendOutAvgDays,
    }));
  }, [trends]);

  const isLoading = summaryLoading || trendsLoading;

  // Get key H&E and IHC stats for summary cards
  const heStats = summary?.turnaroundByStainType.find(
    (t) => t.stainType === "H&E"
  );
  const ihcStats = summary?.turnaroundByStainType.find(
    (t) => t.stainType === "IHC"
  );

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Group align="flex-end" wrap="wrap" gap="md">
          <Select
            label="Stain Type"
            placeholder="All stain types"
            data={STAIN_TYPE_OPTIONS}
            value={stainType}
            onChange={setStainType}
            clearable
            w={200}
          />
          <Select
            label="Facility"
            placeholder="All facilities"
            data={facilityOptions}
            value={facilityId}
            onChange={setFacilityId}
            clearable
            searchable
            w={220}
          />
          <ReportDateRangePicker value={dateRange} onChange={setDateRange} />
        </Group>
      </Paper>

      {isLoading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : (
        <>
          {/* Summary Cards */}
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            <Paper p="md" withBorder>
              <Group gap="xs" mb="xs">
                <ThemeIcon size="sm" variant="light" color="blue">
                  <IconClock size="0.9rem" />
                </ThemeIcon>
                <Text size="sm" c="dimmed">
                  Avg TAT (H&E)
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {heStats?.avgTatHours ?? 0} hrs
              </Text>
              <Text size="xs" c="dimmed">
                Target: {heStats?.targetHours ?? 4}h |{" "}
                {heStats?.withinTargetPct ?? 0}% within target
              </Text>
            </Paper>
            <Paper p="md" withBorder>
              <Group gap="xs" mb="xs">
                <ThemeIcon size="sm" variant="light" color="green">
                  <IconFlask size="0.9rem" />
                </ThemeIcon>
                <Text size="sm" c="dimmed">
                  Avg TAT (IHC)
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {ihcStats?.avgTatHours ?? 0} hrs
              </Text>
              <Text size="xs" c="dimmed">
                Target: {ihcStats?.targetHours ?? 6}h |{" "}
                {ihcStats?.withinTargetPct ?? 0}% within target
              </Text>
            </Paper>
            <Paper p="md" withBorder>
              <Group gap="xs" mb="xs">
                <ThemeIcon size="sm" variant="light" color="orange">
                  <IconTarget size="0.9rem" />
                </ThemeIcon>
                <Text size="sm" c="dimmed">
                  Rush Volume
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {summary?.rushVolume.rushPct ?? 0}%
              </Text>
              <Text size="xs" c="dimmed">
                {summary?.rushVolume.rushCount ?? 0} rush /{" "}
                {summary?.rushVolume.total ?? 0} total cases
              </Text>
            </Paper>
            <Paper p="md" withBorder>
              <Group gap="xs" mb="xs">
                <ThemeIcon size="sm" variant="light" color="red">
                  <IconAlertTriangle size="0.9rem" />
                </ThemeIcon>
                <Text size="sm" c="dimmed">
                  QC Failure Rate
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {summary?.qcFailureRate.failureRate ?? 0}%
              </Text>
              <Text size="xs" c="dimmed">
                {summary?.qcFailureRate.failedSlides ?? 0} failures /{" "}
                {summary?.qcFailureRate.totalSlides ?? 0} slides
              </Text>
            </Paper>
          </SimpleGrid>

          {/* TAT by Stain Type (Bar Chart) */}
          {tatChartData.length > 0 && (
            <Paper p="lg" withBorder>
              <Text fw={600} mb="md">
                Turnaround Time by Stain Type
              </Text>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={tatChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stainType" />
                  <YAxis
                    label={{
                      value: "Hours",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <RechartsTooltip />
                  <Legend />
                  <Bar
                    dataKey="Avg TAT (hrs)"
                    fill="#228be6"
                    name="Avg TAT (hrs)"
                  />
                  <Bar
                    dataKey="Target (hrs)"
                    fill="#dee2e6"
                    name="Target (hrs)"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Monthly Trends (Line Chart) */}
          {trendsChartData.length > 0 && (
            <Paper p="lg" withBorder>
              <Text fw={600} mb="md">
                Monthly TAT Trends by Stain Type
              </Text>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendsChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis
                    label={{
                      value: "Hours",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <RechartsTooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="H&E"
                    stroke={STAIN_TYPE_COLORS["H&E"]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="IHC"
                    stroke={STAIN_TYPE_COLORS.IHC}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="ISH"
                    stroke={STAIN_TYPE_COLORS.ISH}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Special"
                    stroke={STAIN_TYPE_COLORS.Special}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <ReferenceLine
                    y={4}
                    stroke="#228be6"
                    strokeDasharray="5 5"
                    label="H&E Target"
                  />
                  <ReferenceLine
                    y={6}
                    stroke="#40c057"
                    strokeDasharray="5 5"
                    label="IHC Target"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Send-Out Turnaround by Provider */}
          {summary?.sendOutTurnaround &&
            summary.sendOutTurnaround.length > 0 && (
              <Paper p="lg" withBorder>
                <Group gap="xs" mb="md">
                  <ThemeIcon size="sm" variant="light" color="violet">
                    <IconTruckDelivery size="0.9rem" />
                  </ThemeIcon>
                  <Text fw={600}>Send-Out Turnaround by Provider</Text>
                </Group>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Provider</Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>
                        Avg TAT
                      </Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>
                        Expected
                      </Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>Total</Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>
                        Completed
                      </Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>
                        Overdue
                      </Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>
                        Status
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {summary.sendOutTurnaround.map((row) => {
                      const isOverExpected =
                        row.expectedTatDays > 0 &&
                        row.avgTatDays > row.expectedTatDays;
                      return (
                        <Table.Tr key={row.providerId}>
                          <Table.Td fw={500}>{row.providerName}</Table.Td>
                          <Table.Td style={{ textAlign: "center" }}>
                            <Badge
                              variant="light"
                              color={isOverExpected ? "orange" : "green"}
                            >
                              {row.avgTatDays} days
                            </Badge>
                          </Table.Td>
                          <Table.Td style={{ textAlign: "center" }}>
                            {row.expectedTatDays > 0
                              ? `${row.expectedTatDays} days`
                              : "-"}
                          </Table.Td>
                          <Table.Td style={{ textAlign: "center" }}>
                            {row.totalSendOuts}
                          </Table.Td>
                          <Table.Td style={{ textAlign: "center" }}>
                            {row.completedSendOuts}
                          </Table.Td>
                          <Table.Td style={{ textAlign: "center" }}>
                            {row.overdueCount > 0 ? (
                              <Badge variant="filled" color="red" size="sm">
                                {row.overdueCount}
                              </Badge>
                            ) : (
                              <Badge variant="light" color="green" size="sm">
                                0
                              </Badge>
                            )}
                          </Table.Td>
                          <Table.Td style={{ textAlign: "center" }}>
                            <Badge
                              variant="filled"
                              color={
                                row.overdueCount > 0
                                  ? "red"
                                  : isOverExpected
                                  ? "yellow"
                                  : "green"
                              }
                              size="sm"
                            >
                              {row.overdueCount > 0
                                ? "Overdue"
                                : isOverExpected
                                ? "Slow"
                                : "On Track"}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}

          {/* QC Failure Breakdown by Stain Type */}
          {summary?.qcFailureRate &&
            summary.qcFailureRate.failuresByStainType.length > 0 && (
              <Paper p="lg" withBorder>
                <Group gap="xs" mb="md">
                  <ThemeIcon size="sm" variant="light" color="red">
                    <IconAlertTriangle size="0.9rem" />
                  </ThemeIcon>
                  <Text fw={600}>QC Failure Rate by Stain Type</Text>
                </Group>
                <Stack gap="sm">
                  {summary.qcFailureRate.failuresByStainType.map((row) => (
                    <div key={row.stainType}>
                      <Group justify="space-between" mb={4}>
                        <Text size="sm">
                          {STAIN_TYPE_LABELS[row.stainType] || row.stainType}
                        </Text>
                        <Text size="sm" fw={500}>
                          {row.count} failures ({row.rate}%)
                        </Text>
                      </Group>
                      <Progress
                        value={row.rate}
                        color={
                          row.rate > 5
                            ? "red"
                            : row.rate > 2
                            ? "orange"
                            : "green"
                        }
                        size="md"
                      />
                    </div>
                  ))}
                </Stack>
              </Paper>
            )}

          {/* TAT Details Table */}
          {summary?.turnaroundByStainType &&
            summary.turnaroundByStainType.length > 0 && (
              <Paper p="lg" withBorder>
                <Group gap="xs" mb="md">
                  <ThemeIcon size="sm" variant="light" color="blue">
                    <IconChartLine size="0.9rem" />
                  </ThemeIcon>
                  <Text fw={600}>TAT Details by Stain Type</Text>
                </Group>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Stain Type</Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>
                        Avg TAT
                      </Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>
                        Target
                      </Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>
                        Total Slides
                      </Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>
                        Completed
                      </Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>
                        Within Target
                      </Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>
                        % Within Target
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {summary.turnaroundByStainType.map((row) => {
                      const meetsTarget = row.avgTatHours <= row.targetHours;
                      return (
                        <Table.Tr key={row.stainType}>
                          <Table.Td fw={500}>
                            {STAIN_TYPE_LABELS[row.stainType] || row.stainType}
                          </Table.Td>
                          <Table.Td style={{ textAlign: "center" }}>
                            <Badge
                              variant="light"
                              color={meetsTarget ? "green" : "orange"}
                            >
                              {row.avgTatHours} hrs
                            </Badge>
                          </Table.Td>
                          <Table.Td style={{ textAlign: "center" }}>
                            {row.targetHours} hrs
                          </Table.Td>
                          <Table.Td style={{ textAlign: "center" }}>
                            {row.totalSlides}
                          </Table.Td>
                          <Table.Td style={{ textAlign: "center" }}>
                            {row.completedSlides}
                          </Table.Td>
                          <Table.Td style={{ textAlign: "center" }}>
                            {row.withinTarget}
                          </Table.Td>
                          <Table.Td style={{ textAlign: "center" }}>
                            <Badge
                              variant="filled"
                              color={
                                row.withinTargetPct >= 90
                                  ? "green"
                                  : row.withinTargetPct >= 70
                                  ? "yellow"
                                  : "red"
                              }
                              size="sm"
                            >
                              {row.withinTargetPct}%
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}

          {/* Empty state */}
          {(!summary ||
            (summary.turnaroundByStainType.length === 0 &&
              summary.rushVolume.total === 0)) && (
            <Center py="xl">
              <Stack align="center" gap="xs">
                <ThemeIcon size="xl" variant="light" color="gray">
                  <IconFileSpreadsheet size="1.5rem" />
                </ThemeIcon>
                <Text fw={500} c="dimmed">
                  No staining data for this period
                </Text>
                <Text size="sm" c="dimmed">
                  Adjust the date range or filters to see metrics
                </Text>
              </Stack>
            </Center>
          )}
        </>
      )}
    </Stack>
  );
}

export default function StainMetricsPage() {
  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg" p="md">
          <Group>
            <Button
              component={Link}
              to="/reports"
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
            >
              Reports
            </Button>
          </Group>
          <StainMetricsContent />
        </Stack>
      </Box>
    </Container>
  );
}
